import { useState } from "react";
import {
  getSourceDiscover,
  getSourceDashboard,
  getSourceGroups,
  getTelemetry,
  runSourceCheck,
  saveSourceGroups,
  setSourceEnabled,
  type SourceDiscoverResult,
  type SourceManifest,
  type SourceStatus,
  type TelemetryEvent,
  type UserSourceGroup,
  type UserSpace,
} from "../api";

interface SourceDashboardWorkspaceOptions {
  user: UserSpace | null;
  setBusy: (busy: boolean) => void;
  setMessage: (message: string) => void;
  onSourcesChanged: () => void;
}

export function useSourceDashboardWorkspace({
  user,
  setBusy,
  setMessage,
  onSourcesChanged,
}: SourceDashboardWorkspaceOptions) {
  const [sources, setSources] = useState<SourceManifest[]>([]);
  const [enabledSourceIds, setEnabledSourceIds] = useState<string[]>([]);
  const [sourceStatuses, setSourceStatuses] = useState<SourceStatus[]>([]);
  const [sourceDiscoverById, setSourceDiscoverById] = useState<Record<string, SourceDiscoverResult>>({});
  const [sourceHomeDiscoverById, setSourceHomeDiscoverById] = useState<Record<string, SourceDiscoverResult>>({});
  const [sourceGroups, setSourceGroups] = useState<UserSourceGroup[]>([]);
  const [discoverLoadingSourceId, setDiscoverLoadingSourceId] = useState<string | null>(null);
  const [sourceActionBusyId, setSourceActionBusyId] = useState<string | null>(null);
  const [telemetry, setTelemetry] = useState<TelemetryEvent[]>([]);

  async function initializeSources(userId: string, manifests: SourceManifest[]) {
    setSources(manifests);
    await refreshMonitor(userId);
  }

  async function refreshMonitor(userId = user?.id) {
    if (!userId) return;
    const dashboard = await getSourceDashboard(userId);
    const events = await getTelemetry(userId);
    const groups = await getSourceGroups(userId);

    setSources(dashboard.sources);
    setEnabledSourceIds(dashboard.enabledSourceIds);
    setSourceStatuses(dashboard.statuses);
    setSourceGroups(groups);
    setTelemetry(events.slice(-6).reverse());
  }

  async function handleSaveSourceGroups(groups: Array<Omit<UserSourceGroup, "userId" | "updatedAt">>) {
    if (!user) return;
    const saved = await saveSourceGroups(user.id, groups);
    setSourceGroups(saved);
  }

  async function handleToggleSource(sourceId: string) {
    if (!user || sourceActionBusyId === sourceId) return;

    const wasEnabled = enabledSourceIds.includes(sourceId);
    const nextEnabled = !wasEnabled;

    setSourceActionBusyId(sourceId);
    setEnabledSourceIds(current => setSourceEnabledState(current, sourceId, nextEnabled));
    setMessage(`${nextEnabled ? "正在启用" : "正在停用"} ${sourceId}`);

    try {
      await setSourceEnabled(user.id, sourceId, nextEnabled);
      onSourcesChanged();
      setMessage(`${sourceId} 已${nextEnabled ? "启用" : "停用"}`);
      void refreshMonitor(user.id).catch(error => {
        setMessage(error instanceof Error ? error.message : String(error));
      });
    } catch (error) {
      setEnabledSourceIds(current => setSourceEnabledState(current, sourceId, wasEnabled));
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setSourceActionBusyId(null);
    }
  }

  async function handleCheckSource(sourceId: string) {
    if (!user) return;

    setBusy(true);
    setMessage(`正在检查 ${sourceId}`);

    try {
      await runSourceCheck(sourceId);
      await refreshMonitor(user.id);
      setMessage(`${sourceId} 检查完成`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function handleLoadSourceDiscover(sourceId: string, targetUrl?: string) {
    const currentDiscover = sourceDiscoverById[sourceId];
    const homeDiscover = sourceHomeDiscoverById[sourceId];
    const showingHomeDiscover = currentDiscover && isHomeDiscover(currentDiscover);

    if (!targetUrl && homeDiscover) {
      setSourceDiscoverById(current => ({
        ...current,
        [sourceId]: homeDiscover,
      }));
      return;
    }

    if (!user || (!targetUrl && showingHomeDiscover) || discoverLoadingSourceId === sourceId) return;

    setDiscoverLoadingSourceId(sourceId);

    try {
      const discover = await getSourceDiscover(sourceId, user.id, targetUrl);
      setSourceDiscoverById(current => ({
        ...current,
        [sourceId]: discover,
      }));
      if (!targetUrl) {
        setSourceHomeDiscoverById(current => ({
          ...current,
          [sourceId]: discover,
        }));
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setDiscoverLoadingSourceId(null);
    }
  }

  return {
    sources,
    enabledSourceIds,
    sourceStatuses,
    sourceDiscoverById,
    sourceHomeDiscoverById,
    sourceGroups,
    discoverLoadingSourceId,
    sourceActionBusyId,
    telemetry,
    initializeSources,
    refreshMonitor,
    handleToggleSource,
    handleCheckSource,
    handleLoadSourceDiscover,
    handleSaveSourceGroups,
  };
}

function isHomeDiscover(discover: SourceDiscoverResult): boolean {
  if (!discover.currentUrl) return true;

  const url = new URL(discover.currentUrl);
  return url.pathname === "/";
}

function setSourceEnabledState(sourceIds: string[], sourceId: string, enabled: boolean): string[] {
  if (enabled) return sourceIds.includes(sourceId) ? sourceIds : [...sourceIds, sourceId];
  return sourceIds.filter(item => item !== sourceId);
}
